import classNames from 'classnames';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as FaIcons from 'react-icons/fa';
import { Button } from '@/Domain/components/Shared/Button/Button';

const iconEntries = Object.entries(FaIcons).filter(([name]) => name.startsWith('Fa'));

type Props = {
  iconName: string;
  iconColor: string;
  onChange: (name: string, color: string) => void;
};

const COLORS = ['#00A357', '#EF4444', '#5F58FE', '#E2AC03', '#8B5CF6', '#6B7280'];

export const FullIconPicker = ({ iconName, iconColor, onChange }: Props) => {
  const { t } = useTranslation('icon_picker');
  const [currentIcon, setCurrentIcon] = useState(iconName);
  const [currentColor, setCurrentColor] = useState(iconColor || COLORS[5]);

  const handleChangeIcon = (name: string) => {
    setCurrentIcon(name);

    return onChange(name, currentColor);
  };

  const handelChangeColor = (color: string) => {
    setCurrentColor(color);

    return onChange(currentIcon, color);
  };

  return (
    <div className="max-w-md flex flex-col gap-2" data-testid="select-icon-picker">
      <p className="text-foreground text-sm font-medium">{t('customize_icon')}</p>
      <section>
        <p className="text-foreground-subtle text-xs mb-2">{t('choose_color')}</p>
        <div className="grid grid-cols-6 gap-2.5">
          {COLORS.map((color) => (
            <div
              className={classNames('size-8 rounded-lg cursor-pointer', {
                'ring !ring-opacity-25 !border !border-foreground !ring-ring':
                  color === currentColor,
              })}
              style={{ backgroundColor: color }}
              key={color}
              onClick={() => handelChangeColor(color)}
            >
              <Check
                className={classNames('m-auto h-full text-white', {
                  hidden: color !== currentColor,
                })}
              />
            </div>
          ))}
        </div>
      </section>
      <section className="mt-2">
        <p className="text-foreground-subtle text-xs mb-2">{t('choose_icon')}</p>
        <div className="grid grid-cols-6 gap-x-2.5 gap-y-1.5 max-h-[160px] overflow-y-auto hide-scrollbar">
          {iconEntries.map(([name, Icon]) => (
            <Button
              size="icon"
              variant="custom"
              key={name}
              onClick={() => handleChangeIcon(name)}
              className={classNames('m-0.5 rounded border', {
                'ring !ring-opacity-25 !border !border-foreground !ring-ring': currentIcon === name,
              })}
            >
              <Icon size={20} color={currentColor} />
            </Button>
          ))}
        </div>
      </section>
    </div>
  );
};
